import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
    Activity, 
    Menu, 
    X, 
    User, 
    LogOut, 
    History, 
    Stethoscope,
    LayoutDashboard,
    Sun,
    Moon,
    CreditCard,
    BookOpen,
    MapPin,
    Settings,
    Video,
    UserPlus,
    Pill,
    BookHeart,
    Crown,
    Share2,
    Download
} from 'lucide-react';
import { toast } from 'sonner';

export const Layout = ({ children }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBanner, setShowInstallBanner] = useState(false);

    // Check if already installed as PWA
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    useEffect(() => {
        // Don't show install banner if already installed
        if (isInstalled) return;

        // Listen for beforeinstallprompt event
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallBanner(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Show banner for iOS after a delay (they can't auto-prompt)
        if (isIOS) {
            const timer = setTimeout(() => setShowInstallBanner(true), 3000);
            return () => clearTimeout(timer);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, [isInstalled, isIOS]);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                toast.success('App installed successfully!');
                setShowInstallBanner(false);
            }
            setDeferredPrompt(null);
        } else if (isIOS) {
            toast.info(
                <div className="space-y-2">
                    <p className="font-semibold">Install on iOS</p>
                    <p className="text-sm">Tap the Share button, then "Add to Home Screen"</p>
                </div>,
                { duration: 6000 }
            );
        } else {
            toast.info(
                <div className="space-y-2">
                    <p className="font-semibold">Install MediGuide</p>
                    <p className="text-sm">Look for the install icon in your browser's address bar, or use the browser menu</p>
                </div>,
                { duration: 6000 }
            );
        }
    };

    const handleShareApp = async () => {
        const shareData = {
            title: 'MediGuide - AI Health Assessment',
            text: 'Get instant AI-powered symptom analysis and health recommendations. Free, private, and secure.',
            url: window.location.origin
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                toast.success('Thanks for sharing MediGuide!');
            } else {
                await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}\n\n${shareData.url}`);
                toast.success('Link copied to clipboard!');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                try {
                    await navigator.clipboard.writeText(window.location.origin);
                    toast.success('Link copied to clipboard!');
                } catch {
                    toast.error('Could not share. Please copy the URL manually.');
                }
            }
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const navLinks = isAuthenticated ? [
        { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
        { path: '/symptom-check', label: t('checkSymptoms'), icon: Stethoscope },
        { path: '/history', label: t('history'), icon: History },
        { path: '/health-library', label: t('healthLibrary'), icon: BookOpen },
        { path: '/health-diary', label: t('healthDiary'), icon: BookHeart },
        { path: '/consultations', label: t('consultations'), icon: Video },
        { path: '/referrals', label: t('referrals'), icon: UserPlus },
        { path: '/prescriptions', label: t('prescriptions'), icon: Pill },
        { path: '/nearby', label: t('findCare'), icon: MapPin },
    ] : [];

    return (
        <div className="min-h-screen flex flex-col">
            {/* Navigation */}
            <header className="sticky top-0 z-50 glass-card border-b border-slate-200 dark:border-slate-700">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <Link 
                            to={isAuthenticated ? '/dashboard' : '/'} 
                            className="flex items-center gap-2 group"
                            data-testid="logo-link"
                        >
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center shadow-lg shadow-primary/20">
                                <Activity className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-heading font-bold text-xl text-foreground">
                                Medi<span className="text-primary">Guide</span>
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-8">
                            {navLinks.map(({ path, label, icon: Icon }) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors
                                        ${location.pathname === path 
                                            ? 'text-primary active' 
                                            : 'text-muted-foreground hover:text-foreground'}`}
                                    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                </Link>
                            ))}
                        </div>

                        {/* Right side */}
                        <div className="flex items-center gap-2">
                            {/* Share Button - Always visible */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleShareApp}
                                className="rounded-full"
                                title="Share MediGuide"
                                data-testid="share-nav-btn"
                            >
                                <Share2 className="w-5 h-5" />
                            </Button>

                            {/* Install Button - Show when not installed */}
                            {!isInstalled && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleInstallClick}
                                    className="rounded-full text-primary"
                                    title="Install App"
                                    data-testid="install-nav-btn"
                                >
                                    <Download className="w-5 h-5" />
                                </Button>
                            )}

                            {/* Theme Toggle */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleTheme}
                                className="rounded-full"
                                data-testid="theme-toggle"
                            >
                                {theme === 'light' ? (
                                    <Moon className="w-5 h-5" />
                                ) : (
                                    <Sun className="w-5 h-5" />
                                )}
                            </Button>

                            {isAuthenticated ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            className="flex items-center gap-2"
                                            data-testid="user-menu-trigger"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                <User className="w-4 h-4 text-primary" />
                                            </div>
                                            <span className="hidden sm:inline text-sm font-medium">
                                                {user?.name}
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        <DropdownMenuItem className="text-muted-foreground">
                                            {user?.email}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        {(user?.role === 'owner' || user?.subscription?.plan === 'owner') && (
                                            <DropdownMenuItem 
                                                onClick={() => navigate('/owner')}
                                                className="cursor-pointer text-purple-600 dark:text-purple-400"
                                            >
                                                <Crown className="w-4 h-4 mr-2" />
                                                Owner Panel
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem 
                                            onClick={() => navigate('/settings')}
                                            className="cursor-pointer"
                                        >
                                            <Settings className="w-4 h-4 mr-2" />
                                            {t('settings')}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                            onClick={() => navigate('/subscription')}
                                            className="cursor-pointer"
                                        >
                                            <CreditCard className="w-4 h-4 mr-2" />
                                            {t('subscription')}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={handleShareApp}
                                            className="cursor-pointer"
                                        >
                                            <Share2 className="w-4 h-4 mr-2" />
                                            {t('shareApp')}
                                        </DropdownMenuItem>
                                        {!isInstalled && (
                                            <DropdownMenuItem 
                                                onClick={handleInstallClick}
                                                className="cursor-pointer text-primary"
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                {t('installApp')}
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={handleLogout}
                                            className="text-destructive cursor-pointer"
                                            data-testid="logout-button"
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            {t('logout')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <div className="hidden sm:flex items-center gap-3">
                                    <Link to="/login">
                                        <Button variant="ghost" data-testid="login-nav-button">
                                            {t('login')}
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button 
                                            className="rounded-full shadow-lg shadow-primary/20"
                                            data-testid="register-nav-button"
                                        >
                                            {t('register')}
                                        </Button>
                                    </Link>
                                </div>
                            )}

                            {/* Mobile menu button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                data-testid="mobile-menu-toggle"
                            >
                                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </Button>
                        </div>
                    </div>

                    {/* Mobile Navigation */}
                    {mobileMenuOpen && (
                        <div className="md:hidden py-4 border-t border-slate-200 dark:border-slate-700">
                            <div className="flex flex-col gap-2">
                                {navLinks.map(({ path, label, icon: Icon }) => (
                                    <Link
                                        key={path}
                                        to={path}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                                            ${location.pathname === path 
                                                ? 'bg-primary/10 text-primary' 
                                                : 'text-muted-foreground hover:bg-muted'}`}
                                        data-testid={`mobile-nav-${label.toLowerCase().replace(' ', '-')}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {label}
                                    </Link>
                                ))}
                                
                                {/* Share & Install in Mobile Menu */}
                                <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
                                    <button
                                        onClick={() => {
                                            handleShareApp();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-left text-muted-foreground hover:bg-muted"
                                        data-testid="mobile-share-btn"
                                    >
                                        <Share2 className="w-4 h-4" />
                                        {t('shareApp')}
                                    </button>
                                    {!isInstalled && (
                                        <button
                                            onClick={() => {
                                                handleInstallClick();
                                                setMobileMenuOpen(false);
                                            }}
                                            className="flex items-center gap-3 px-3 py-2 rounded-lg w-full text-left text-primary hover:bg-primary/10"
                                            data-testid="mobile-install-btn"
                                        >
                                            <Download className="w-4 h-4" />
                                            {t('installApp')}
                                        </button>
                                    )}
                                </div>

                                {!isAuthenticated && (
                                    <>
                                        <Link 
                                            to="/login" 
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="px-3 py-2 text-muted-foreground hover:text-foreground"
                                        >
                                            {t('login')}
                                        </Link>
                                        <Link 
                                            to="/register" 
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            <Button className="w-full rounded-full">{t('register')}</Button>
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </nav>
            </header>

            {/* Main Content */}
            <main className="flex-1">
                {children}
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-200 dark:border-slate-700 bg-card">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Activity className="w-5 h-5 text-primary" />
                            <span className="font-heading font-semibold">MediGuide</span>
                        </div>
                        <p className="text-sm text-muted-foreground text-center">
                            This is an AI-powered tool for informational purposes only. 
                            Always consult a healthcare professional for medical advice.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            © {new Date().getFullYear()} MediGuide
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
